import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-client";
import { policyKeys } from "@/lib/query";
import { relatedPoliciesSchema } from "@/schemas/policy-dialogs";

interface RelatedPoliciesDialogProps {
  policyNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RelatedPoliciesDialog({
  policyNumber,
  open,
  onOpenChange,
}: RelatedPoliciesDialogProps) {
  const {
    data: policies,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: policyKeys.related(policyNumber),
    queryFn: () =>
      apiGet(`/api/policies/${encodeURIComponent(policyNumber)}/related`, relatedPoliciesSchema),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Related Policies</DialogTitle>
          <DialogDescription>
            Policies sharing the same FEIN/Tax ID as {policyNumber}
          </DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load related policies"
          isEmpty={!isLoading && !isError && policies !== undefined && policies.length === 0}
          emptyIcon={FileText}
          emptyMessage="No related policies found."
          skeletonHeight="h-10"
        />

        {policies && policies.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2 font-medium">Policy Number</th>
                  <th className="px-2 py-2 font-medium">Insured Name</th>
                  <th className="px-2 py-2 font-medium">FEIN/Tax ID</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => {
                  const isCurrent = p.policyNumber === policyNumber;
                  return (
                    <tr
                      key={p.policyNumber}
                      className={
                        isCurrent ? "border-b bg-muted/50 last:border-0" : "border-b last:border-0"
                      }
                    >
                      <td className="px-2 py-2 font-medium">
                        {isCurrent ? (
                          <span className="inline-flex items-center gap-2">
                            <span>{p.policyNumber}</span>
                            <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                              This policy
                            </span>
                          </span>
                        ) : (
                          <Link
                            to="/policy/$policyNumber"
                            params={{ policyNumber: p.policyNumber }}
                            className="text-primary hover:underline"
                            onClick={() => onOpenChange(false)}
                          >
                            {p.policyNumber}
                          </Link>
                        )}
                      </td>
                      <td className="px-2 py-2">{p.insuredName}</td>
                      <td className="px-2 py-2 font-mono text-xs">{p.feinTaxId}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
